import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Stethoscope,
  FlaskConical,
  UserCheck,
  Sprout,
  ShieldCheck,
  Gem,
  Eye,
  Compass,
} from 'lucide-react';
import { TEAM } from '../mock';
import SEO from '../components/SEO';

const ABOUT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Akira Signature Care',
  isPartOf: { '@id': 'https://akirasignaturecare.com/#website' },
  about: { '@id': 'https://akirasignaturecare.com/#clinic' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://akirasignaturecare.com/' },
      { '@type': 'ListItem', position: 2, name: 'About', item: 'https://akirasignaturecare.com/about' },
    ],
  },
};

const CORE_VALUES = [
  {
    icon: Stethoscope,
    name: 'Physician-Led Care',
    description: 'Every treatment is directed by a physician — not delegated.',
  },
  {
    icon: FlaskConical,
    name: 'Science-Based Practice',
    description: 'Evidence-based protocols. FDA-cleared technologies. No compromise.',
  },
  {
    icon: UserCheck,
    name: 'Individualized Treatment',
    description: "Each plan is tailored to the patient's clinical needs, history, and goals.",
  },
  {
    icon: Sprout,
    name: 'Regenerative Approach',
    description: "We support the body's own healing mechanisms for natural, lasting results.",
  },
  {
    icon: ShieldCheck,
    name: 'Clinical Safety',
    description: 'Conservative, ethical practice. Patient wellbeing above all else.',
  },
  {
    icon: Gem,
    name: 'Boutique Experience',
    description: 'Unhurried, personalized care — every patient, every visit.',
  },
];

const AboutPage = () => {
  const teamMembers = TEAM;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="about-page">
      <SEO
        title="About & Team"
        description="Physician-led, regenerative boutique medical clinic in Alhambra, CA. Meet Angela Trias, M.D., and our aesthetic nursing team."
        path="/about"
        schema={ABOUT_SCHEMA}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 text-rose-300 text-sm mb-6">
              <Sparkles className="w-4 h-4" />
              <span>OUR STORY</span>
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl mb-4">
              About Akira Signature Care
            </h1>
            <p className="text-base lg:text-lg text-rose-200/90 tracking-[0.2em] uppercase mb-6">
              Physician-Led Regenerative Boutique Clinic · Alhambra, CA
            </p>
            <p className="text-xl text-stone-300 leading-relaxed italic">
              Where Science Meets Aesthetic Excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Vision + Mission */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto">
            {/* Vision */}
            <div
              data-testid="about-vision-card"
              className="bg-stone-50 border-l-4 border-rose-400 p-10 rounded-r-2xl"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-rose-500" />
                </div>
                <div className="text-rose-500 text-xs tracking-[0.3em] uppercase font-medium">
                  Our Vision
                </div>
              </div>
              <p className="font-serif text-2xl text-stone-900 leading-snug">
                To be the clinic where patients feel truly seen — where science serves people,
                and every treatment is a reflection of the physician's commitment to their
                well-being.
              </p>
            </div>

            {/* Mission */}
            <div
              data-testid="about-mission-card"
              className="bg-stone-50 border-l-4 border-rose-400 p-10 rounded-r-2xl"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <Compass className="w-5 h-5 text-rose-500" />
                </div>
                <div className="text-rose-500 text-xs tracking-[0.3em] uppercase font-medium">
                  Our Mission
                </div>
              </div>
              <p className="font-serif text-2xl text-stone-900 leading-snug">
                To provide every patient with science-based, individually tailored treatment —
                physician-led from consultation to outcome, in a setting that honors their time,
                comfort, and trust.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Principle / Pull Quote */}
      <section className="py-20 bg-gradient-to-br from-stone-900 to-rose-950 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-rose-300 text-xs tracking-[0.4em] uppercase mb-6">
              Our Promise
            </div>
            <blockquote
              className="font-serif text-3xl lg:text-4xl leading-relaxed mb-6"
              data-testid="about-founder-principle"
            >
              "At Akira Signature Care, you are not a chart number — you are a patient with a
              story, and your care reflects that."
            </blockquote>
            <div className="text-rose-300 text-sm tracking-[0.3em] uppercase">
              Every visit · Every treatment · Physician-led
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-rose-500 text-xs tracking-[0.4em] uppercase mb-4">
              Core Values
            </div>
            <h2 className="font-serif text-4xl text-stone-900 mb-4">
              What We Stand For
            </h2>
            <p className="text-base text-stone-600 leading-relaxed">
              Six principles guide every decision we make — from the technologies we invest in
              to the way every patient is welcomed at the door.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {CORE_VALUES.map((value, i) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.name}
                  data-testid={`core-value-${value.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="group bg-stone-50 hover:bg-white border border-stone-100 hover:border-rose-200 hover:shadow-lg transition-all duration-300 p-8 rounded-2xl"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-rose-100 group-hover:bg-rose-400 flex items-center justify-center mb-5 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-rose-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-serif text-xl text-stone-900 mb-3">{value.name}</h3>
                  <p className="text-stone-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-20 bg-stone-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-rose-500 text-xs tracking-[0.4em] uppercase mb-4">
              Our Physicians & Team
            </div>
            <h2 className="font-serif text-4xl text-stone-900 mb-4">Meet Our Team</h2>
            <p className="text-base text-stone-600 max-w-3xl mx-auto leading-relaxed">
              A unique blend of aesthetic mastery and precision medicine — bringing together
              the best of both worlds under one roof.
            </p>
          </div>

          {/* Team Grid */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <div key={index} data-testid={`about-team-card-${member.id || index}`} className="group bg-white rounded-2xl overflow-hidden border border-stone-200 shadow-lg hover:border-rose-300 hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-500 ease-out">
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-stone-900 mb-1">{member.name}</h3>
                  <p className="text-rose-600 font-medium text-sm mb-4">{member.title}</p>
                  <p className="text-stone-600 text-sm mb-4">{member.credentials}</p>
                  {member.bio.split('\n\n').map((para, i) => (
                    <p key={i} className="text-stone-700 mb-4 leading-relaxed">{para}</p>
                  ))}
                  {member.bioDisclaimer && (
                    <p className="text-stone-500 italic text-xs mb-4 leading-relaxed" data-testid={`team-disclaimer-${member.id || index}`}>
                      {member.bioDisclaimer}
                    </p>
                  )}

                  <div className="space-y-2">
                    <div className="font-semibold text-stone-900 text-sm">Specialties:</div>
                    <div className="flex flex-wrap gap-2">
                      {member.specialties.map((specialty, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-stone-100 text-stone-700 text-xs rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  {member.education && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-stone-600 text-xs">{member.education}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-stone-900 to-rose-900 text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl mb-6">Ready to Experience the Akira Difference?</h2>
          <p className="text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
            Book your consultation and discover how we can help you achieve your aesthetic and wellness goals.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-rose-400 hover:bg-rose-500 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
            data-testid="about-cta-contact"
          >
            Schedule Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
